import orderBy from "lodash.orderby";
import { SquareArrowOutUpRight, X } from "lucide-react";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import {
  IgCopyTextTypography,
  IgDivider,
  IgIconButton,
  IgTypography,
} from "@intelligems/ig-theme";
import { ScheduleDto, TabEnumFe } from "@intelligems/ig-types";
import { officialCategoryName, shortId } from "@intelligems/ig-utils";
import { Drawer, Grid, useTheme } from "@mui/material";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import { useGetExperience } from "@src/api/experiences/get-experience";
import { useGetSourceExperience } from "@src/api/experiences/get-source-experience";
import { useGetOrg } from "@src/api/organizations/get-organization";
import { useIsAdmin } from "@src/api/user-auths/is-admin";
import { EXPERIMENT_ROUTES, PERSONALIZATION_ROUTES } from "@src/routes/routes";
import {
  closeDrawer,
  getDrawer,
  isDrawerOpen,
} from "@src/store/slices/drawers";
import { Experience } from "@src/store/slices/experiences/types";
import { setDialog } from "@src/store/slices/global";
import { fullDateTimeConverter } from "@src/utils/dateConverters";

interface PersonalizationScheduleInfoProps {
  experience: Experience;
  schedules: ScheduleDto[];
}

interface SourceExperienceInfoProps {
  experience: Experience;
}

const SourceExperienceInfo = ({ experience }: SourceExperienceInfoProps) => {
  const sourceExperience = useGetSourceExperience(
    experience?.sourceExperienceId || undefined
  );
  const theme = useTheme();
  const navigate = useNavigate();
  const onActionClick = (exp: Experience) => {
    const navString = `${
      exp.sourceAction === "duplicate" && exp.category === "personalization"
        ? PERSONALIZATION_ROUTES.Personalization
        : EXPERIMENT_ROUTES.Experiment
    }/${exp.sourceExperienceId}?action=edit&tab=${TabEnumFe.ANALYTICS}`;
    void navigate(navString);
  };

  if (sourceExperience.isLoading) return null;

  return (
    <>
      <IgDivider spacing={"m"} />
      <Grid item>
        <IgTypography color={theme.palette.text.secondary}>
          History
        </IgTypography>
      </Grid>

      <Grid item container direction={"row"} alignItems={"center"}>
        <Grid item>
          <IgTypography fontWeight={600}>
            {experience.sourceAction === "rollout"
              ? "Rolled out"
              : "Duplicated"}{" "}
            {!sourceExperience.data ? (
              <IgTypography color={theme.palette.text.secondary}>
                from deleted {officialCategoryName(experience.category, false)}
              </IgTypography>
            ) : (
              <IgTypography onClick={() => onActionClick(experience)}>
                from {sourceExperience?.data?.experience.name}
              </IgTypography>
            )}
          </IgTypography>
        </Grid>
        {sourceExperience.data && (
          <Grid item>
            <IgIconButton size={"small"} sx={{ marginLeft: "4px" }}>
              <SquareArrowOutUpRight size={16} />
            </IgIconButton>
          </Grid>
        )}
      </Grid>
    </>
  );
};

const PersonalizationScheduleInfo = ({
  experience,
  schedules,
}: PersonalizationScheduleInfoProps) => {
  const org = useGetOrg();
  const storeCurrencyConfig = useConfiguration<{
    code: string;
    locale: string;
  }>("shop_currency");
  const dispatch = useDispatch();
  const theme = useTheme();

  if (schedules.length === 0) return null;

  const startAtSchedule =
    schedules.find((schedule) => schedule.action === "start") || null;

  const pauseAtSchedule =
    schedules.find((schedule) => schedule.action === "pause") || null;

  return (
    <>
      <IgDivider spacing={"m"} />
      <Grid
        item
        container
        direction={"row"}
        justifyContent={"space-between"}
        alignItems={"center"}
      >
        <Grid item mb={1}>
          <IgTypography color={theme.palette.text.secondary}>
            Schedule{schedules.length > 1 ? "s" : ""}
          </IgTypography>
        </Grid>
        <Grid item>
          <IgTypography
            onClick={() => {
              dispatch(
                setDialog({
                  dialog: "scheduleTest",
                  open: true,
                  props: {
                    experience,
                  },
                })
              );
              dispatch(closeDrawer("experienceInfo"));
            }}
            igStyle={"linkNoBold"}
          >
            Edit
          </IgTypography>
        </Grid>
      </Grid>
      <Grid item>
        {schedules.length === 1 && schedules[0].action === "start" ? (
          <IgTypography>
            Scheduled to enable at{" "}
            {fullDateTimeConverter(
              schedules[0].executionTime,
              org.data.org?.timezone ?? undefined,
              storeCurrencyConfig?.value?.locale
            )}{" "}
          </IgTypography>
        ) : schedules.length === 1 && schedules[0].action === "pause" ? (
          <IgTypography>
            Scheduled to disable at{" "}
            {fullDateTimeConverter(
              schedules[0].executionTime,
              org.data.org?.timezone ?? undefined,
              storeCurrencyConfig?.value?.locale
            )}{" "}
          </IgTypography>
        ) : (
          <Grid item container direction={"column"} rowSpacing={1}>
            <Grid item>
              <IgTypography>
                Scheduled to{" "}
                <IgTypography fontWeight={600}>enable</IgTypography> at{" "}
                {fullDateTimeConverter(
                  startAtSchedule?.executionTime,
                  org.data.org?.timezone ?? undefined,
                  storeCurrencyConfig?.value?.locale
                )}
              </IgTypography>
            </Grid>
            <Grid item>
              <IgTypography>
                Scheduled to{" "}
                <IgTypography fontWeight={600}>disable</IgTypography> at{" "}
                {fullDateTimeConverter(
                  pauseAtSchedule?.executionTime,
                  org.data.org?.timezone ?? undefined,
                  storeCurrencyConfig?.value?.locale
                )}
              </IgTypography>
            </Grid>
          </Grid>
        )}
      </Grid>
    </>
  );
};

export const ExperienceInfoDrawer = () => {
  const isInfoDrawerOpen = useSelector(isDrawerOpen("experienceInfo"));
  const drawerProps = useSelector(getDrawer("experienceInfo"));

  const dispatch = useDispatch();
  const handleClose = () => {
    dispatch(closeDrawer("experienceInfo"));
  };
  const isAdmin = useIsAdmin();
  const theme = useTheme();
  const onIdCopy = async (id: string, name: string) => {
    await navigator.clipboard.writeText(id);
    toast.success(`${name} ${id.length === 12 ? "GA" : ""} ID Copied`);
  };

  const { experience } = drawerProps || {};

  if (!experience) return null;

  return (
    <Drawer
      anchor="right"
      open={isInfoDrawerOpen}
      onClose={handleClose}
      sx={{
        zIndex: 1400,
        ".MuiDrawer-paper": {
          borderRadius: "0 !important",
          width: "450px",
          padding: 5,
        },
      }}
    >
      <Grid container direction={"column"}>
        <Grid
          item
          container
          direction={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
          mb={4}
        >
          <Grid item xs={10}>
            <IgTypography variant={"h2"}>{experience.name}</IgTypography>
          </Grid>
          <Grid item>
            <IgIconButton onClick={handleClose}>
              <X size={20} />
            </IgIconButton>
          </Grid>
        </Grid>
        <Grid item container direction={"column"} rowSpacing={1}>
          <Grid item mb={1}>
            <IgTypography color={theme.palette.text.secondary}>
              Information
            </IgTypography>
          </Grid>
          <Grid item>
            <IgTypography fontWeight={600}>
              {officialCategoryName(experience.category, true)} ID:{" "}
              <IgCopyTextTypography
                text={experience.id!}
                toastMessage={(text) =>
                  void onIdCopy(
                    text,
                    officialCategoryName(experience.category, true)
                  )
                }
              />
            </IgTypography>
          </Grid>
          {experience.category === "personalization" && !isAdmin
            ? null
            : orderBy(experience.variations, (_) => _.order).map(
                (variation) => (
                  <Grid
                    item
                    key={variation.id}
                    container
                    direction={"column"}
                    rowSpacing={1}
                  >
                    <Grid item>
                      <IgTypography igStyle={"semibold"}>
                        {experience.category === "personalization"
                          ? "Variation"
                          : variation.name}{" "}
                        ID:{" "}
                        <IgCopyTextTypography
                          text={variation.id}
                          toastMessage={(text) =>
                            void onIdCopy(
                              text,
                              experience.category === "personalization"
                                ? "Variation"
                                : variation.name
                            )
                          }
                        />
                      </IgTypography>
                    </Grid>
                    <Grid item>
                      <IgTypography ml={4}>For Google Analytics: </IgTypography>
                      <IgCopyTextTypography
                        text={shortId(variation.id)}
                        toastMessage={(text) =>
                          void onIdCopy(
                            text,
                            experience.category === "personalization"
                              ? "Variation"
                              : variation.name
                          )
                        }
                      />
                    </Grid>
                  </Grid>
                )
              )}
          {experience.category === "personalization" &&
          experience.variations[0].offerId ? (
            <Grid item>
              <IgTypography fontWeight={600}>
                Offer ID:{" "}
                <IgCopyTextTypography
                  text={experience.id!}
                  toastMessage={(text) => void onIdCopy(text, "Offer")}
                />
              </IgTypography>
            </Grid>
          ) : null}
        </Grid>
        {experience.sourceExperienceId && experience.sourceAction ? (
          <Grid item>
            <SourceExperienceInfo experience={experience} />
          </Grid>
        ) : null}
        {experience.category === "personalization" ? (
          <Grid item>
            <PersonalizationScheduleInfo
              experience={experience}
              schedules={[]}
            />
          </Grid>
        ) : null}
      </Grid>
    </Drawer>
  );
};
